import { LockupStacked } from "./Mark";
import { ScrollReveal } from "./ScrollReveal";

const glossary = [
  {
    term: "Vihangam",
    devanagari: "विहंगम",
    meaning: "Like a bird — the soul that soars beyond the material world",
  },
  {
    term: "Yog",
    devanagari: "योग",
    meaning:
      "Union — the science of uniting individual consciousness with the Supreme. Distinct from physical yoga (postures)",
  },
  {
    term: "Sadguru",
    devanagari: "सद्गुरु",
    meaning:
      "The true Master — one who leads from darkness to light, from the unreal to the real",
  },
  {
    term: "Swarved",
    devanagari: "स्वर्वेद",
    meaning:
      "The holy scripture by Sadguru Sadafal Deo Ji Maharaj — the encyclopaedia of the science of consciousness",
  },
  {
    term: "Brahma Vidyā",
    devanagari: "ब्रह्म विद्या",
    meaning:
      "The supreme knowledge — the science of the Absolute, leading to direct experience of the highest truth",
  },
  {
    term: "Samādhi",
    devanagari: "समाधि",
    meaning:
      "The state of union with the Supreme Being — the highest state of meditative consciousness",
  },
  {
    term: "Satsangh",
    devanagari: "सत्संग",
    meaning:
      "Gathering in truth — a spiritual discourse exploring how meditation transforms daily life",
  },
  {
    term: "Abhyas",
    devanagari: "अभ्यास",
    meaning: "Practice — the consistent, devoted practice of meditation",
  },
];

/**
 * Nightfall — the day of practice ends on ink. The glossary reads as
 * dictionary entries on ruled lines; the mark and the chapter contact
 * close the page quietly.
 */
export function Footer() {
  return (
    <footer className="relative overflow-hidden bg-warm-brown px-7 py-24 md:py-28">
      {/* Cornice — a faint carved scallop where the ink meets the
          page, the way the Mahamandir's cornice meets the night sky. */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[16px]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='26' height='16' viewBox='0 0 26 16'%3E%3Cg fill='none' stroke='rgba(244,240,230,0.4)' stroke-width='1'%3E%3Cpath d='M0 11 Q6.5 0 13 11 Q19.5 0 26 11'/%3E%3Ccircle cx='13' cy='14' r='1' fill='rgba(244,240,230,0.4)' stroke='none'/%3E%3C/g%3E%3C/svg%3E\")",
          backgroundSize: "26px 16px",
          backgroundRepeat: "repeat-x",
        }}
        aria-hidden
      />
      {/* Watermark — the अ ghosted into the ink the way a papermaker's
          mark sits in a sheet. Felt more than read. */}
      <span
        className="font-devanagari pointer-events-none absolute -right-16 -bottom-40 leading-none text-cream select-none"
        style={{ fontSize: "34rem", opacity: 0.035 }}
        aria-hidden
      >
        अ
      </span>

      <div className="relative mx-auto max-w-6xl">
        <ScrollReveal>
          <div className="flex items-baseline gap-4">
            <span className="font-serif text-[0.9375rem] italic text-gold-light">
              ॰
            </span>
            <span className="text-[0.6875rem] font-semibold tracking-[0.28em] text-cream/50 uppercase">
              Glossary — the language of the inner journey
            </span>
            <span className="h-px flex-1 self-center bg-cream/15" aria-hidden />
          </div>
        </ScrollReveal>

        <div className="mt-10 grid gap-x-14 md:grid-cols-2">
          {glossary.map((item) => (
            <ScrollReveal key={item.term}>
              <div className="grid grid-cols-[5.5rem_1fr] items-baseline gap-x-4 border-t border-cream/12 py-5 md:grid-cols-[7rem_1fr]">
                <span className="font-devanagari text-2xl leading-none text-gold-light/80">
                  {item.devanagari}
                </span>
                <div>
                  <h4 className="font-serif text-lg font-medium text-cream">
                    {item.term}
                  </h4>
                  <p className="mt-1 text-sm leading-relaxed text-cream/50">
                    {item.meaning}
                  </p>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>

        <ScrollReveal>
          <blockquote className="mx-auto mt-20 max-w-2xl text-center font-serif text-2xl leading-[1.45] text-cream/70 italic md:text-[1.75rem]">
            &ldquo;The silence within us opens up the door to the universe to
            begin conversing with us.&rdquo;
          </blockquote>
        </ScrollReveal>

        <div className="mt-20 flex flex-col items-center gap-8 border-t border-cream/12 pt-16">
          <LockupStacked
            glyphSize={60}
            wordmarkSize={13}
            taglineSize={13}
            tone="light"
          />

          <div className="space-y-1 text-center">
            <p className="text-[0.65rem] font-semibold tracking-[0.32em] text-cream/45 uppercase">
              Aotearoa chapter
            </p>
            <p className="font-serif text-lg text-gold-light italic">
              Vihangam Yog New Zealand
            </p>
          </div>

          <div className="flex flex-col items-center gap-1.5 text-center text-sm">
            <p className="text-[0.65rem] font-semibold tracking-[0.28em] text-cream/45 uppercase">
              Get in touch
            </p>
            <p className="text-cream/75">Neha · Chapter lead</p>
            <a
              href="tel:+64224731155"
              className="text-cream/75 underline-offset-4 transition-colors duration-300 hover:text-cream hover:underline"
            >
              +64 22 473 1155
            </a>
            <a
              href="mailto:info@vihangamyognz.org"
              className="text-cream/75 underline-offset-4 transition-colors duration-300 hover:text-cream hover:underline"
            >
              info@vihangamyognz.org
            </a>
          </div>

          <a
            href="/events"
            className="text-sm text-cream/55 underline-offset-4 transition-colors duration-300 hover:text-cream hover:underline"
          >
            Events
          </a>

          <p className="text-xs tracking-[0.2em] text-cream/35">
            © {new Date().getFullYear()} Sadguru Sadafaldeo Vihangam Yoga
            Sansthan
          </p>
        </div>
      </div>
    </footer>
  );
}
